import axios, { type AxiosInstance, isAxiosError } from 'axios';
import { extname } from 'path';
import { sleep } from '../utils/index.js';
import type {
  UploadFileRequest,
  UploadTokenResult,
  UploadTokenRequestBody,
  UploadTokenResponseBody,
  UploadTokenErrorBody,
} from './types.js';

// Sync with data-handler ARTIFACT_ALLOWED_FILE_TYPES.
export const DEFAULT_ALLOWED_FILE_TYPES: readonly string[] = ['png', 'jpeg', 'jpg', 'webm', 'zip', 'json', 'txt', 'md'];

export function isAllowedExtension(fileName: string, allowed: readonly string[] = DEFAULT_ALLOWED_FILE_TYPES): boolean {
  const ext = extname(fileName).slice(1).toLowerCase();
  return ext.length > 0 && allowed.includes(ext);
}

// Sync with data-handler ARTIFACT_MAX_SIZE_BYTES. Server 400s the whole batch
// on the first oversized file — pre-filter locally.
export const DEFAULT_MAX_SIZE_BYTES = 50 * 1024 * 1024;

export function isWithinSizeLimit(sizeBytes: number, max: number = DEFAULT_MAX_SIZE_BYTES): boolean {
  return sizeBytes <= max;
}

// 400 → bad batch. 401/403 → auth. 404 → wrong route or missing blob. 405 → method not allowed.
const PERMANENT_CLIENT_STATUSES: readonly number[] = [400, 401, 403, 404, 405];

export interface ArtifactUploadClientOptions {
  token: string;
  serverUrl: string;
  runId: string;
  maxAttempts?: number;
  retryDelay?: number;
  putTimeout?: number;
  tokenRequestTimeout?: number;
  onDebug?: (message: string) => void;
}

export class ArtifactUploadTokenError extends Error {
  readonly kind: 'client' | 'server_error' | 'storage' | 'network';
  readonly httpStatus?: number;
  readonly serverCode?: string;

  constructor(
    message: string,
    kind: 'client' | 'server_error' | 'storage' | 'network',
    httpStatus?: number,
    serverCode?: string
  ) {
    super(message);
    this.name = 'ArtifactUploadTokenError';
    this.kind = kind;
    this.httpStatus = httpStatus;
    this.serverCode = serverCode;
  }
}

export class ArtifactPutError extends Error {
  readonly kind: 'client' | 'server_error' | 'network';
  readonly httpStatus?: number;

  constructor(message: string, kind: 'client' | 'server_error' | 'network', httpStatus?: number) {
    super(message);
    this.name = 'ArtifactPutError';
    this.kind = kind;
    this.httpStatus = httpStatus;
  }
}

export class ArtifactUploadClient {
  private readonly http: AxiosInstance;
  private readonly runId: string;
  private readonly maxAttempts: number;
  private readonly retryDelay: number;
  private readonly putTimeout: number;
  private readonly onDebug: (message: string) => void;

  constructor(options: ArtifactUploadClientOptions) {
    this.runId = options.runId;
    this.maxAttempts = options.maxAttempts ?? 2;
    this.retryDelay = options.retryDelay ?? 1000;
    this.putTimeout = options.putTimeout ?? 60_000;
    this.onDebug = options.onDebug ?? (() => {});

    // data-handler middleware reads only Authorization: Bearer; x-api-key is rejected.
    this.http = axios.create({
      baseURL: options.serverUrl,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${options.token}`,
      },
      timeout: options.tokenRequestTimeout ?? 10_000,
    });
  }

  async requestUploadTokens(files: UploadFileRequest[]): Promise<UploadTokenResult> {
    if (files.length === 0) {
      throw new ArtifactUploadTokenError('Cannot request upload tokens for an empty batch', 'client');
    }

    const body: UploadTokenRequestBody = {
      files: files.map((f) => ({
        name: f.name,
        test_id: f.testId,
        content_type: f.contentType,
        size_bytes: f.sizeBytes,
      })),
    };
    const path = `/api/v1/reporter/runs/${encodeURIComponent(this.runId)}/artifacts/upload-token`;

    let lastError: ArtifactUploadTokenError | null = null;

    for (let attempt = 0; attempt < this.maxAttempts; attempt++) {
      try {
        this.onDebug(`upload-token request attempt ${attempt + 1}/${this.maxAttempts} (files=${files.length})`);
        const response = await this.http.post<UploadTokenResponseBody>(path, body);
        return {
          uploads: response.data.uploads.map((u) => ({
            attachmentId: u.attachment_id,
            blobKey: u.blob_key,
            uploadUrl: u.upload_url,
          })),
          expiresAt: response.data.expires_at,
          maxSizeBytes: response.data.max_size_bytes,
          allowedFileTypes: response.data.allowed_file_types,
        };
      } catch (error) {
        const classified = classifyTokenError(error);
        lastError = classified;
        if (classified.kind === 'client') {
          this.onDebug(
            `upload-token permanent client error (${classified.httpStatus ?? '?'}/${classified.serverCode ?? '?'}) — not retrying`
          );
          throw classified;
        }
        if (attempt < this.maxAttempts - 1) {
          const delay = this.retryDelay * Math.pow(2, attempt);
          this.onDebug(
            `upload-token transient failure (kind=${classified.kind}, status=${classified.httpStatus ?? '?'}) — retrying in ${delay}ms`
          );
          await sleep(delay);
        }
      }
    }

    throw lastError ?? new ArtifactUploadTokenError('Upload-token request failed', 'network');
  }

  async uploadFile(uploadUrl: string, body: Buffer, contentType: string): Promise<void> {
    let lastError: ArtifactPutError | null = null;

    for (let attempt = 0; attempt < this.maxAttempts; attempt++) {
      try {
        await this.doPut(uploadUrl, body, contentType);
        return;
      } catch (error) {
        const classified = classifyPutError(error);
        lastError = classified;
        if (classified.kind === 'client') {
          this.onDebug(`PUT permanent client error (${classified.httpStatus ?? '?'}) — not retrying`);
          throw classified;
        }
        if (attempt < this.maxAttempts - 1) {
          const delay = this.retryDelay * Math.pow(2, attempt);
          this.onDebug(
            `PUT transient failure (kind=${classified.kind}, status=${classified.httpStatus ?? '?'}) — retrying in ${delay}ms`
          );
          await sleep(delay);
        }
      }
    }

    throw lastError ?? new ArtifactPutError('Artifact PUT failed', 'network');
  }

  // Buffer, not stream: caller snapshots before token round-trip so a file
  // Playwright cleans up mid-run can't vanish under an in-flight PUT.
  private async doPut(uploadUrl: string, body: Buffer, contentType: string): Promise<void> {
    await axios.put(uploadUrl, body, {
      headers: {
        'Content-Type': contentType,
        'Content-Length': body.length,
        'x-ms-blob-type': 'BlockBlob',
      },
      timeout: this.putTimeout,
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
  }
}

function classifyTokenError(error: unknown): ArtifactUploadTokenError {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const body = error.response?.data as UploadTokenErrorBody | undefined;
    const code = body?.error?.code;
    const message = body?.error?.message ?? error.message;

    if (status !== undefined && PERMANENT_CLIENT_STATUSES.includes(status)) {
      return new ArtifactUploadTokenError(message, 'client', status, code);
    }
    if (status === 503) return new ArtifactUploadTokenError(message, 'storage', status, code);
    if (status !== undefined && status >= 500) {
      return new ArtifactUploadTokenError(message, 'server_error', status, code);
    }
    if (status !== undefined && status >= 400) {
      // Unusual 4xx (409/429) — treat as client; server contract offers no headroom.
      return new ArtifactUploadTokenError(message, 'client', status, code);
    }
    return new ArtifactUploadTokenError(message, 'network');
  }
  const message = error instanceof Error ? error.message : String(error);
  return new ArtifactUploadTokenError(message, 'network');
}

function classifyPutError(error: unknown): ArtifactPutError {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const message = error.message;

    if (status !== undefined && PERMANENT_CLIENT_STATUSES.includes(status)) {
      return new ArtifactPutError(message, 'client', status);
    }
    // Azure Blob throttles PUTs with 429 — genuine transient, retry.
    if (status === 429) return new ArtifactPutError(message, 'server_error', status);
    if (status !== undefined && status >= 500) return new ArtifactPutError(message, 'server_error', status);
    if (status !== undefined && status >= 400) return new ArtifactPutError(message, 'client', status);
    return new ArtifactPutError(message, 'network');
  }
  const message = error instanceof Error ? error.message : String(error);
  return new ArtifactPutError(message, 'network');
}
